import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';

const DEFAULT_BREAKPOINTS = {
  mobile: 1,
  tablet: 2,
  desktop: 4,
};

const DEFAULT_GAP_REM = 1.5;

function getCardsPerView(breakpoints) {
  if (typeof window === 'undefined') {
    return breakpoints.desktop || DEFAULT_BREAKPOINTS.desktop;
  }

  if (window.innerWidth >= 1280) return breakpoints.desktop || DEFAULT_BREAKPOINTS.desktop;
  if (window.innerWidth >= 768) return breakpoints.tablet || DEFAULT_BREAKPOINTS.tablet;
  return breakpoints.mobile || DEFAULT_BREAKPOINTS.mobile;
}

export default function ContentSlider({
  items,
  renderItem,
  getItemKey,
  breakpoints = DEFAULT_BREAKPOINTS,
  animated = true,
  autoplayMs = 3000,
  transitionMs = 380,
  gapRem = DEFAULT_GAP_REM,
  controlsClassName = '',
  trackClassName = '',
}) {
  const normalizedBreakpoints = useMemo(() => ({
    mobile: breakpoints.mobile || DEFAULT_BREAKPOINTS.mobile,
    tablet: breakpoints.tablet || DEFAULT_BREAKPOINTS.tablet,
    desktop: breakpoints.desktop || DEFAULT_BREAKPOINTS.desktop,
  }), [breakpoints.desktop, breakpoints.mobile, breakpoints.tablet]);

  const [cardsPerView, setCardsPerView] = useState(() => getCardsPerView(normalizedBreakpoints));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  const sliderEnabled = items.length > cardsPerView;
  const gapValue = `${gapRem}rem`;

  useEffect(() => {
    const updateCardsPerView = () => setCardsPerView(getCardsPerView(normalizedBreakpoints));
    updateCardsPerView();
    window.addEventListener('resize', updateCardsPerView);
    return () => window.removeEventListener('resize', updateCardsPerView);
  }, [normalizedBreakpoints]);

  useEffect(() => {
    if (sliderEnabled) {
      setCurrentSlide(cardsPerView);
      setTransitionEnabled(true);
    } else {
      setCurrentSlide(0);
      setTransitionEnabled(true);
    }
  }, [sliderEnabled, cardsPerView, items.length]);

  useEffect(() => {
    if (!sliderEnabled || autoplayMs <= 0) return undefined;

    const interval = window.setInterval(() => {
      setCurrentSlide((prev) => prev + 1);
    }, autoplayMs);

    return () => window.clearInterval(interval);
  }, [sliderEnabled, autoplayMs]);

  const trackItems = useMemo(() => {
    if (!sliderEnabled) return items;
    const head = items.slice(0, cardsPerView);
    const tail = items.slice(-cardsPerView);
    return [...tail, ...items, ...head];
  }, [sliderEnabled, items, cardsPerView]);

  const handlePrevious = () => {
    if (!sliderEnabled) return;
    setCurrentSlide((prev) => prev - 1);
  };

  const handleNext = () => {
    if (!sliderEnabled) return;
    setCurrentSlide((prev) => prev + 1);
  };

  const handleTrackTransitionEnd = () => {
    if (!sliderEnabled) return;

    if (currentSlide >= items.length + cardsPerView) {
      setTransitionEnabled(false);
      setCurrentSlide(cardsPerView);
    } else if (currentSlide < cardsPerView) {
      setTransitionEnabled(false);
      setCurrentSlide(items.length + cardsPerView - 1);
    }
  };

  useEffect(() => {
    if (transitionEnabled) return undefined;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setTransitionEnabled(true));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [transitionEnabled]);

  return (
    <>
      {sliderEnabled && (
        <div className={`flex items-center justify-end gap-2 mb-6 ${controlsClassName}`.trim()}>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handlePrevious}
            className="border-[#fccf1f]/40 text-[#fccf1f] hover:bg-[#fccf1f]/10"
            aria-label="Itens anteriores"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleNext}
            className="border-[#fccf1f]/40 text-[#fccf1f] hover:bg-[#fccf1f]/10"
            aria-label="Próximos itens"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      <div className="overflow-hidden">
        <div
          className={`flex ${trackClassName}`.trim()}
          onTransitionEnd={handleTrackTransitionEnd}
          style={{
            gap: gapValue,
            transform: sliderEnabled
              ? `translateX(calc(-${currentSlide * (100 / cardsPerView)}% - ${currentSlide * gapRem}rem))`
              : 'translateX(0)',
            transition: animated && transitionEnabled
              ? `transform ${transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)`
              : 'none',
            willChange: sliderEnabled ? 'transform' : 'auto',
          }}
        >
          {trackItems.map((item, index) => (
            <div
              key={getItemKey(item, index)}
              className="shrink-0"
              style={{ width: `calc((100% - ${(cardsPerView - 1) * gapRem}rem) / ${cardsPerView})` }}
            >
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
